<template>
   <div>
      <navbar-component />
      <main class="bg-blueGray-100">
         <!-- Hero: ICP problem → PumpLink7 answer -->
         <section
            class="relative overflow-hidden pt-16 py-20 md:py-28"
            :style="{ backgroundColor: '#111827' }"
         >
            <div
               class="absolute inset-0 bg-center bg-cover"
               :style="{ backgroundImage: `url(${heroImage})`, opacity: 0.35 }"
            ></div>
            <div
               class="absolute inset-0"
               :style="{ backgroundColor: 'rgba(0, 0, 0, 0.55)' }"
            ></div>
            <div class="container relative mx-auto px-4 md:px-6">
               <div class="max-w-3xl">
                  <span
                     class="inline-block bg-blue-600 text-white text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-5 shadow-sm"
                  >
                     Frac pump control
                  </span>
                  <h1
                     class="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-5"
                  >
                     PumpLink7 powered by WaveEdge
                  </h1>
                  <p
                     class="text-blueGray-100 text-lg md:text-xl leading-relaxed max-w-2xl mb-4"
                  >
                     Frac pumpers need clear local and remote control, coordinated
                     over-pressure protection, and engine / transmission command
                     that does not fight the operator mid-stage.
                  </p>
                  <p
                     class="text-blueGray-300 text-base md:text-lg leading-relaxed max-w-2xl mb-8"
                  >
                     PumpLink7 is the unit HMI for that job: rate and pressure at
                     a glance, kickout you can trust, detailed drivetrain meters,
                     and the same secure path into Datavan MPC and WaveCommand7.
                  </p>
                  <div class="flex flex-wrap gap-3">
                     <router-link
                        :to="{ path: '/', hash: '#contact' }"
                        class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                     >
                        Contact AMI <i class="fas fa-arrow-right ml-2"></i>
                     </router-link>
                     <router-link
                        to="/waveedge"
                        class="inline-block bg-transparent hover:bg-blueGray-800 text-white font-bold py-3 px-6 rounded-lg border border-blueGray-500 transition-colors duration-200"
                     >
                        About WaveEdge
                     </router-link>
                  </div>
               </div>
            </div>
         </section>

         <!-- ICP problem cards -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Problems pump crews actually face
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     PumpLink7 is aimed at the friction that shows up when every
                     unit must start clean, hold kickout, and hand off between
                     the truck and the van without confusion.
                  </p>
               </div>
               <div class="grid md:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto">
                  <div
                     v-for="item in painPoints"
                     :key="item.title"
                     class="bg-blueGray-900 rounded-xl p-6 border border-blueGray-700"
                  >
                     <div class="flex items-center gap-3 mb-3">
                        <div
                           class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2"
                           :class="item.iconWrapClass"
                        >
                           <i :class="item.icon"></i>
                        </div>
                        <h3 class="text-xl font-bold text-white leading-tight">
                           {{ item.title }}
                        </h3>
                     </div>
                     <p class="text-blueGray-400 text-sm mb-3">
                        <span class="font-semibold text-blueGray-300"
                           >The challenge:</span
                        >
                        {{ item.challenge }}
                     </p>
                     <p class="text-blueGray-200 text-sm md:text-base">
                        <span class="font-semibold text-lightBlue-300"
                           >How PumpLink7 helps:</span
                        >
                        {{ item.solution }}
                     </p>
                  </div>
               </div>
               <div class="text-center max-w-3xl mx-auto mt-12 mb-6">
                  <h3 class="text-2xl md:text-3xl font-bold text-white mb-3">
                     Operation screen—engine, gear, and pressure in one view
                  </h3>
                  <p
                     class="text-blueGray-300 text-base md:text-lg leading-relaxed"
                  >
                     Start / stop, RPM and gear, Local / Remote authority, pump
                     rate, discharge and kickout pressure—built for the pumper
                     panel and the same workflow when you take control from the
                     van.
                  </p>
               </div>
               <div
                  class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-900 max-w-5xl mx-auto"
               >
                  <img
                     :src="opHomeImage"
                     alt="PumpLink7 Operation screen showing RPM, gear, and pump meters"
                     class="w-full h-auto block"
                  />
                  <p
                     class="p-3 text-center text-sm text-blueGray-400 tracking-wide"
                  >
                     Operation screen—primary control and live meters
                  </p>
               </div>
            </div>
         </section>

         <!-- Over-pressure / kickout -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div
                  class="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto"
               >
                  <div>
                     <h2
                        class="text-3xl md:text-4xl font-bold text-white mb-4"
                     >
                        Kickout and over-pressure you can see and trust
                     </h2>
                     <p
                        class="text-blueGray-300 text-lg mb-5 leading-relaxed"
                     >
                        When discharge pressure crosses the kickout setpoint,
                        PumpLink7 drives the unit to neutral idle and surfaces
                        the event clearly—so the crew is not guessing which
                        pump tripped or why.
                     </p>
                     <ul class="space-y-2">
                        <li
                           v-for="bullet in kickoutBullets"
                           :key="bullet"
                           class="flex items-start gap-3 text-blueGray-200"
                        >
                           <i
                              class="fas fa-check-circle text-emerald-400 mt-1 flex-shrink-0"
                           ></i>
                           <span>{{ bullet }}</span>
                        </li>
                     </ul>
                  </div>
                  <div
                     class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-800"
                  >
                     <img
                        :src="overpressureImage"
                        alt="PumpLink7 Operation screen during over-pressure kickout"
                        class="w-full h-auto block"
                     />
                     <p
                        class="p-3 text-center text-sm text-blueGray-400 tracking-wide"
                     >
                        Over-pressure state with kickout notification
                     </p>
                  </div>
               </div>
            </div>
         </section>

         <!-- Engine / transmission detail + maintenance -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Drivetrain detail and maintenance that stay on the truck
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     Beyond the Operation screen, operators get live engine,
                     transmission, and pump meters—plus hour and cycle totals
                     that make service decisions less dependent on tribal
                     knowledge.
                  </p>
               </div>
               <div class="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                  <div
                     v-for="shot in detailShots"
                     :key="shot.title"
                     class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-900"
                  >
                     <img
                        :src="shot.image"
                        :alt="shot.alt"
                        class="w-full h-auto block"
                     />
                     <div class="p-4">
                        <h3 class="text-lg font-bold text-white mb-1">
                           {{ shot.title }}
                        </h3>
                        <p class="text-blueGray-300 text-sm">{{ shot.text }}</p>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         <!-- Capabilities + deployments + WaveEdge -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Built for the pad ecosystem
                  </h2>
                  <p class="text-lg text-blueGray-300">
                     Same robust pump controls, with the flexibility of WaveEdge
                     and a clear path into AMI van and cloud products.
                  </p>
               </div>
               <div class="grid md:grid-cols-2 gap-5 md:gap-6 mb-10 max-w-5xl mx-auto">
                  <div
                     v-for="feature in features"
                     :key="feature.title"
                     class="bg-blueGray-800 rounded-xl p-5 md:p-6 shadow-sm border border-blueGray-700"
                  >
                     <div class="flex items-center gap-3 mb-3">
                        <div
                           class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2"
                           :class="feature.iconWrapClass"
                        >
                           <i :class="feature.icon"></i>
                        </div>
                        <h3
                           class="text-xl md:text-2xl font-bold text-white leading-tight"
                        >
                           {{ feature.title }}
                        </h3>
                     </div>
                     <p
                        class="text-blueGray-300 text-sm md:text-base leading-relaxed"
                     >
                        <template v-if="feature.linkTo">
                           {{ feature.textBefore
                           }}<router-link
                              :to="feature.linkTo"
                              class="text-lightBlue-300 underline hover:text-lightBlue-200"
                              >{{ feature.linkText }}</router-link
                           >{{ feature.textAfter }}
                        </template>
                        <template v-else>{{ feature.text }}</template>
                     </p>
                  </div>
               </div>

               <div class="grid lg:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto mb-12">
                  <div
                     v-for="deployment in deployments"
                     :key="deployment.title"
                     class="bg-blueGray-800 p-6 md:p-7 rounded-xl border border-blueGray-700"
                  >
                     <div class="flex items-start">
                        <div
                           class="w-12 h-12 rounded-full flex items-center justify-center mr-4 border-2 flex-shrink-0"
                           :class="deployment.iconWrapClass"
                        >
                           <i :class="deployment.icon"></i>
                        </div>
                        <div>
                           <p
                              class="text-xs font-bold uppercase tracking-wide mb-1"
                              :class="deployment.labelClass"
                           >
                              {{ deployment.label }}
                           </p>
                           <h3 class="text-xl font-bold text-white">
                              {{ deployment.title }}
                           </h3>
                           <p class="text-blueGray-300 mt-2">
                              {{ deployment.text }}
                           </p>
                        </div>
                     </div>
                  </div>
               </div>

               <p class="text-center text-blueGray-400 mb-10">
                  Learn about the redesigned
                  <router-link
                     to="/mpc"
                     class="text-lightBlue-300 underline hover:text-lightBlue-200"
                     >Datavan MPC</router-link
                  >.
               </p>

               <div
                  class="bg-blueGray-800 rounded-2xl p-8 md:p-10 border border-blueGray-700 text-center max-w-3xl mx-auto"
               >
                  <h3 class="text-2xl font-bold text-white mb-4">
                     Powered by WaveEdge
                  </h3>
                  <p class="text-blueGray-300 mb-6">
                     PumpLink7 is built on the WaveEdge industrial runtime—secure
                     command and control, flexible I/O, and a clear path into
                     WaveData Cloud and WaveCommand7.
                  </p>
                  <router-link
                     to="/waveedge"
                     class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                  >
                     Learn more about WaveEdge
                     <i class="fas fa-arrow-right ml-2"></i>
                  </router-link>
               </div>
            </div>
         </section>
      </main>
      <footer-component />
   </div>
</template>

<script>
import NavbarComponent from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import { setPageSeo } from "@/utils/seo";
import heroImage from "@/assets/img/pumplink7/op-home.png";
import opHomeImage from "@/assets/img/pumplink7/op-home.png";
import overpressureImage from "@/assets/img/pumplink7/op-overpressure.png";
import engineTabImage from "@/assets/img/pumplink7/engine-tab.png";
import maintenanceImage from "@/assets/img/pumplink7/maintenance.png";

export default {
   name: "PumpLink7",
   components: {
      NavbarComponent,
      FooterComponent,
   },
   data() {
      return {
         heroImage,
         opHomeImage,
         overpressureImage,
         // ICP challenge → solution cards grounded in the operator manual.
         painPoints: [
            {
               title: "Local vs remote handoff",
               icon: "fas fa-exchange-alt",
               challenge:
                  "Who has command—the truck panel or the van—and can the crew see it without radio guessing?",
               solution:
                  "Clear Local / Remote status on the Operation screen, with the same control workflow at the pumper or from Datavan MPC / WaveCommand7.",
               iconWrapClass: "bg-cyan-900 text-cyan-300 border-cyan-400",
            },
            {
               title: "Over-pressure mid-stage",
               icon: "fas fa-exclamation-triangle",
               challenge:
                  "When discharge or wellhead pressure spikes, delayed reaction or unclear kickout costs iron and time.",
               solution:
                  "Per-pumper kickout setpoints plus coordinated over-pressure detection—unit goes to neutral idle with an on-screen notification.",
               iconWrapClass: "bg-orange-900 text-orange-300 border-orange-400",
            },
            {
               title: "Engine and transmission control",
               icon: "fas fa-tachometer-alt",
               challenge:
                  "Operators need start/stop, RPM, gear, and Neutral Idle without digging through menus under pressure.",
               solution:
                  "Direct engine and transmission control on the Operation screen, with deeper Engine / Transmission / Pump meter tabs when diagnosis matters.",
               iconWrapClass:
                  "bg-blue-900 text-lightBlue-300 border-lightBlue-400",
            },
            {
               title: "Service and hour tracking",
               icon: "fas fa-wrench",
               challenge:
                  "Fluid end, packing, and pump hours live in notebooks or tribal knowledge until something fails.",
               solution:
                  "Maintenance totals and hours-since-service on the unit HMI—authorized resets after work is done.",
               iconWrapClass: "bg-emerald-900 text-emerald-300 border-emerald-500",
            },
         ],
         kickoutBullets: [
            "Per-pumper kickout pressure setpoint on the Operation screen",
            "Coordinated over-pressure detection across wellhead and discharge",
            "Automatic move to Neutral Idle when kickout triggers",
            "On-screen notification so the crew sees the trip immediately",
         ],
         detailShots: [
            {
               title: "Engine / Transmission meters",
               alt: "PumpLink7 Engine tab with live drivetrain meters",
               image: engineTabImage,
               text: "Live engine, transmission, and pump meters—including optional DGB and DEF views when licensed.",
            },
            {
               title: "Maintenance totals",
               alt: "PumpLink7 Maintenance screen with service hours",
               image: maintenanceImage,
               text: "Pumping hours, engine hours, and hours since last service for fluid end, power end, valve, packing, and seat.",
            },
         ],
         // Deployment blocks for local and remote operation.
         deployments: [
            {
               label: "Local Interface",
               title: "Integrated HMI Systems",
               icon: "fas fa-desktop",
               text: "Direct pump control via industrial touchscreens with low-latency visual feedback.",
               labelClass: "text-cyan-300",
               iconWrapClass: "bg-cyan-900 text-cyan-200 border-cyan-400",
            },
            {
               label: "Remote Ready",
               title: "Headless WebHMIs",
               icon: "fas fa-cloud",
               text: "Secure browser-based remote pump control from any device for distributed infrastructure and control centers.",
               labelClass: "text-lightBlue-300",
               iconWrapClass:
                  "bg-blue-900 text-lightBlue-200 border-lightBlue-400",
            },
         ],
         // Core product capabilities.
         features: [
            {
               title: "Fully Compatible",
               icon: "fas fa-sync-alt",
               textBefore: "Still fully compatible with AMI's DVCommand and the redesigned ",
               linkText: "Datavan MPC",
               linkTo: "/mpc",
               textAfter: ".",
               iconWrapClass: "bg-emerald-900 text-emerald-300 border-emerald-500",
            },
            {
               title: "Robust Controls",
               icon: "fas fa-sliders-h",
               text: "The same robust controls for engine and transmission, plus logging, remote operation, and viewing.",
               iconWrapClass:
                  "bg-blue-900 text-lightBlue-300 border-lightBlue-400",
            },
            {
               title: "Enhanced Flexibility",
               icon: "fas fa-project-diagram",
               text: "More flexible I/O and protocols, headless mode, secure command and control, and native cloud integration.",
               iconWrapClass: "bg-amber-900 text-amber-300 border-amber-400",
            },
            {
               title: "Advanced CANBUS",
               icon: "fas fa-network-wired",
               text: "Expanded CANBUS features with better bus filtering, full canTRACE logging, and CANBUS playback to remotely simulate or troubleshoot equipment.",
               iconWrapClass: "bg-orange-900 text-orange-300 border-orange-400",
            },
         ],
      };
   },
   mounted() {
      setPageSeo({
         title: "PumpLink7 | Frac Pump Control Hardware powered by WaveEdge | AMI",
         description:
            "PumpLink7 solves real pump-crew problems: clear Local/Remote handoff, coordinated over-pressure kickout, engine and transmission control, and on-unit maintenance tracking—powered by WaveEdge.",
         keywords:
            "PumpLink7, frac pump control, pump control, remote pump control, kickout, over pressure, PumpLink, WaveEdge, Datavan MPC, AMI",
      });
   },
};
</script>
